import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import JournalCard from "@/components/journal/JournalCard";
import { getAllPosts, getPostsByLanguage, JournalPost } from "@/lib/journal";
import { SEOLanguage } from "@/data/seoTranslations";
import { Loader2 } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";

const Journal: React.FC = () => {
  const { language, t } = useLanguage();
  const [posts, setPosts] = useState<JournalPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const fetchedPosts = await getPostsByLanguage(language as SEOLanguage);
        setPosts(fetchedPosts);
      } catch (error) {
        console.error("Failed to load journal posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [language]);

  const title = language === "de" 
    ? "Das Alpen-Journal" 
    : language === "fr" 
      ? "Le Journal Alpin" 
      : "The Alpine Journal";
      
  const description = language === "de"
    ? "Geschichten über Wellness, Abenteuer und das Leben in den Schweizer Alpen."
    : language === "fr"
      ? "Histoires sur le bien-être, l'aventure et la vie dans les Alpes suisses."
      : "Stories about wellness, adventure, and life in the Swiss Alps.";

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": title,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-alpine-gold/30">
      <Helmet>
        <title>{title} | Beatenberg Resort & Spa</title>
        <meta name="description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(reviewSchema)}
        </script>
      </Helmet>

      <NewHeader />

      <main>
        {/* Elegant Hero Section with Parallax Feel */}
        <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="/images/journal/wellness-hero.webp" 
              onError={(e) => e.currentTarget.src = "/images/hotel-front-view.webp"}
              alt="Swiss Alps Journal" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
          </div>
          
          <div className="relative z-10 container mx-auto px-4 text-center">
            <span className="inline-block py-1 px-3 border border-white/40 rounded-full text-white/90 text-sm tracking-widest uppercase mb-6 bg-black/20 backdrop-blur-sm">
              {language === "de" ? "Offizieller Blog" : "Official Blog"}
            </span>
            <h1 className="text-5xl md:text-7xl font-playfair font-bold text-white mb-6 drop-shadow-lg leading-tight">
              {title}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto font-light leading-relaxed">
              {description}
            </p>
          </div>
        </section>

                <div className="container mx-auto px-4 max-w-7xl -mt-20 relative z-20 pb-20">
                  <div className="mb-6">
                    <Breadcrumbs />
                  </div>
                  {loading ? (
            <div className="flex justify-center items-center py-40 bg-white rounded-xl shadow-xl">
              <Loader2 className="w-8 h-8 animate-spin text-alpine-gold" />
            </div>
          ) : posts.length > 0 ? (
            <div className="bg-white rounded-xl shadow-xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
                {posts.map((post) => (
                  <JournalCard key={post.slug} post={post} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-40 bg-white rounded-xl shadow-xl">
              <p className="text-gray-400 text-xl font-light italic">
                {language === "de" 
                  ? "Momentan sind keine Artikel verfügbar." 
                  : "No stories available at the moment."}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Journal;
